import React, { useRef, useState } from "react";
import { Sparkles, Loader2, UploadCloud, UserPlus, RefreshCw, UserMinus } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const Group = ({ icon: Icon, color, title, names, testid }) => (
  <div className="rounded-lg border border-slate-200 p-3" data-testid={testid}>
    <p className={`flex items-center gap-2 text-sm font-semibold ${color}`}><Icon className="h-4 w-4" /> {title} ({names.length})</p>
    {names.length > 0 && <p className="mt-1 max-h-28 overflow-y-auto text-xs text-slate-500">{names.join(" · ")}</p>}
  </div>
);

export function EmployeeSyncImport({ onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);
  const ref = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/employees/import-pdf", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 240000 });
      setPlan(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha na leitura por IA");
    } finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/employees/import-pdf/${plan.job_id}/apply`);
      toast.success(`${data.created} novo(s), ${data.updated} atualizado(s), ${data.removed} removido(s)`);
      setOpen(false); setPlan(null);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => { setPlan(null); setOpen(true); }}
        className="border-emerald-300 bg-emerald-50/50 text-emerald-700 hover:bg-emerald-100" data-testid="employee-sync-btn">
        <Sparkles className="mr-1.5 h-4 w-4" /> Importar PDF (IA)
      </Button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]" data-testid="employee-sync-dialog">
          <DialogHeader><DialogTitle className="text-[#0B2545]">{plan ? "Confira antes de aplicar" : "Atualizar colaboradores pelo PDF"}</DialogTitle></DialogHeader>
          {!plan ? (
            <>
              <p className="text-sm text-slate-500">Envie o PDF com a lista atual de colaboradores. A lista do PDF passa a ser a oficial: novos são incluídos, existentes atualizados e <b className="text-rose-600">quem não estiver no PDF é apagado junto com seus dados</b> (treinamentos, ASO, EPI, O.S., matrículas EAD e integrações). Alunos externos de cursos não são afetados.</p>
              <div onClick={() => !busy && ref.current?.click()} data-testid="employee-sync-dropzone"
                className="mt-2 flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-emerald-200 bg-emerald-50/40 p-8 text-center hover:border-emerald-400">
                {busy ? <Loader2 className="h-7 w-7 animate-spin text-emerald-600" /> : <UploadCloud className="h-7 w-7 text-emerald-600" />}
                <p className="text-sm font-medium text-slate-700">{busy ? "A IA está lendo o PDF..." : "Clique para enviar (PDF/imagem)"}</p>
                <input ref={ref} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="employee-sync-input" />
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">A IA encontrou <b>{plan.total}</b> colaborador(es) no PDF.</p>
              <Group icon={UserPlus} color="text-emerald-700" title="Novos" names={plan.new} testid="employee-sync-new" />
              <Group icon={RefreshCw} color="text-sky-700" title="Atualizados" names={plan.update} testid="employee-sync-update" />
              <Group icon={UserMinus} color="text-rose-700" title="Serão apagados (com seus dados)" names={plan.remove.map((r) => r.name)} testid="employee-sync-remove" />
            </div>
          )}
          {plan && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setPlan(null)} disabled={busy}>Reenviar</Button>
              <Button onClick={apply} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="employee-sync-apply">
                {busy ? "Aplicando..." : "Confirmar e aplicar"}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
